import React from 'react';
import { Label } from './ui/label';
import { SimpleNativeSelect } from './ui/NativeSelect';

const formatF = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0);

export const computeFraisSplit = (comp, amount) => {
  let taxable;
  if (comp.taxable_calculation_type === 'fixed' && comp.fixed_taxable_amount > 0) {
    taxable = Math.min(amount, comp.fixed_taxable_amount);
  } else if (comp.is_taxable) {
    taxable = amount * (comp.taxable_rate ?? 1.0);
  } else {
    taxable = 0;
  }
  return { taxable, exempt: Math.max(0, amount - taxable) };
};

const rateLabel = (comp) => {
  if (comp.taxable_calculation_type === 'fixed' && comp.fixed_taxable_amount > 0) {
    return `${comp.fixed_taxable_amount.toLocaleString()} F imposables fixes`;
  }
  if (!comp.is_taxable) return 'Non imposable';
  const r = comp.taxable_rate ?? 1.0;
  if (r >= 1) return 'Imposable 100%';
  if (r === 0) return 'Non imposable';
  const pct = Math.abs(r - 0.33333) < 0.005 ? '33,333' : Math.abs(r - 0.66667) < 0.005 ? '66,667' : Math.round(r * 100);
  return `${pct}% imposable`;
};

export const FraisSelector = ({ salaryComponents, employeeForm, setEmployeeForm }) => {
  const fraisList = employeeForm.frais || [];
  const options = salaryComponents
    .filter((c) => c.type === 'frais')
    .map((comp) => ({ value: comp.id, label: `${comp.name} (${rateLabel(comp)})` }));

  const handleAdd = (value) => {
    const component = salaryComponents.find((c) => c.id === value);
    setEmployeeForm((prev) => {
      if ((prev.frais || []).includes(value)) return prev;
      return {
        ...prev,
        frais: [...(prev.frais || []), value],
        component_amounts: { ...prev.component_amounts, [value]: component?.amount || 0 }
      };
    });
  };

  const handleRemove = (fraisId) => {
    setEmployeeForm((prev) => {
      const newAmounts = { ...prev.component_amounts };
      delete newAmounts[fraisId];
      return { ...prev, frais: (prev.frais || []).filter((id) => id !== fraisId), component_amounts: newAmounts };
    });
  };

  return (
    <div className="mt-4">
      <Label>Frais</Label>
      <SimpleNativeSelect
        value=""
        onValueChange={handleAdd}
        placeholder="Ajouter un frais"
        options={options}
      />
      <div className="flex flex-wrap gap-2 mt-2">
        {fraisList.map((fraisId) => {
          const comp = salaryComponents.find((c) => c.id === fraisId);
          if (!comp) return null;
          const currentAmount = employeeForm.component_amounts?.[fraisId] ?? comp.amount ?? 0;
          const { taxable, exempt } = computeFraisSplit(comp, parseFloat(currentAmount) || 0);
          return (
            <div key={`form-frais-${fraisId}`} data-testid={`frais-card-${fraisId}`} style={{ backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 10px', minWidth: '260px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>{comp.name}</span>
                <button type="button" onClick={() => handleRemove(fraisId)} data-testid={`frais-remove-${fraisId}`} style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>✕</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="number"
                  data-testid={`frais-amount-input-${fraisId}`}
                  value={currentAmount}
                  onChange={(e) => {
                    const newAmount = parseFloat(e.target.value) || 0;
                    setEmployeeForm((prev) => ({ ...prev, component_amounts: { ...prev.component_amounts, [fraisId]: newAmount } }));
                  }}
                  placeholder="0"
                  style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', fontWeight: 600 }}
                />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>FCFA</span>
              </div>
              <p data-testid={`frais-split-${fraisId}`} style={{ fontSize: '11px', marginTop: '4px', fontWeight: 600 }}>
                <span style={{ color: '#b45309' }}>Imposable : {formatF(Math.round(taxable))}</span>
                {' • '}
                <span style={{ color: '#15803d' }}>Exonéré : {formatF(Math.round(exempt))}</span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
